// Cold Gear — Sign studio (see blocks/cg-sign-studio.liquid).
// Base sign PNG/WebP (transparent bg) → optional AI rename (sign API) → chroma key →
// alpha contour → extruded 3D slab textured with the photo → GLB → model-viewer AR.
import * as THREE from 'https://cdn.jsdelivr.net/npm/three@0.160.0/+esm';
import { GLTFExporter } from 'https://cdn.jsdelivr.net/npm/three@0.160.0/examples/jsm/exporters/GLTFExporter.js/+esm';
import { contours as d3contours } from 'https://cdn.jsdelivr.net/npm/d3-contour@4.0.2/+esm';

const NAME_RE = /[^A-Z0-9.'&-]/g; // one word: no spaces (the name is cut as one piece)
const store = {
  get(k, d) { try { return JSON.parse(localStorage.getItem(k)) ?? d; } catch (e) { return d; } },
  set(k, v) { try { localStorage.setItem(k, JSON.stringify(v)); } catch (e) {} },
};
const loadImg = (src) => new Promise((res, rej) => {
  const i = new Image(); if (/^https?:|^\/\//.test(src)) i.crossOrigin = 'anonymous';
  i.onload = () => res(i); i.onerror = () => rej(new Error('Could not load image')); i.src = src;
});

function onGreen(img) {
  const sc = Math.min(1, 1280 / Math.max(img.width, img.height));
  const w = Math.round(img.width * sc) + 160, h = Math.round(img.height * sc) + 160;
  const c = document.createElement('canvas'); c.width = w; c.height = h;
  const g = c.getContext('2d'); g.fillStyle = '#00FF00'; g.fillRect(0, 0, w, h); g.drawImage(img, 80, 80, w - 160, h - 160);
  return c.toDataURL('image/jpeg', 0.92);
}
function keyGreen(img) {
  const c = document.createElement('canvas'); c.width = img.width; c.height = img.height;
  const g = c.getContext('2d'); g.drawImage(img, 0, 0);
  const im = g.getImageData(0, 0, c.width, c.height), d = im.data;
  let x0 = 1e9, y0 = 1e9, x1 = 0, y1 = 0;
  for (let i = 0; i < d.length; i += 4) {
    const r = d[i], gg = d[i + 1], b = d[i + 2], m = Math.max(r, b), k = gg - m;
    let a = 255; if (gg > 90 && k > 70) a = 0; else if (k > 25) { a = Math.round(255 * (70 - k) / 45); d[i + 1] = m; }
    d[i + 3] = a;
    if (a > 128) { const p = i / 4, x = p % c.width, y = (p / c.width) | 0; if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y; }
  }
  g.putImageData(im, 0, 0);
  const o = document.createElement('canvas'); o.width = x1 - x0 + 16; o.height = y1 - y0 + 16;
  o.getContext('2d').drawImage(c, x0 - 8, y0 - 8, o.width, o.height, 0, 0, o.width, o.height);
  return o;
}
function edgeColor(img) {
  const w = 300, h = Math.round(300 * img.height / img.width), c = document.createElement('canvas'); c.width = w; c.height = h;
  const g = c.getContext('2d'); g.drawImage(img, 0, 0, w, h); const d = g.getImageData(0, 0, w, h).data;
  let r = 0, gg = 0, b = 0, n = 0;
  for (let y = 1; y < h - 1; y++) for (let x = 1; x < w - 1; x++) {
    const i = (y * w + x) * 4; if (d[i + 3] < 200) continue;
    if (d[i - 1] < 60 || d[i + 7] < 60 || d[i + 3 - w * 4] < 60 || d[i + 3 + w * 4] < 60) { r += d[i]; gg += d[i + 1]; b += d[i + 2]; n++; }
  }
  return n ? new THREE.Color(`rgb(${Math.round(r / n)},${Math.round(gg / n)},${Math.round(b / n)})`) : new THREE.Color('#eee');
}

// image (with alpha) → GLB blob URL of a slab `inches` wide, `mm` thick
async function buildGLB(img, inches, mm = 6) {
  const gridW = 480, s = gridW / img.width, gw = gridW + 8, gh = Math.ceil(img.height * s) + 8;
  const mc = document.createElement('canvas'); mc.width = gw; mc.height = gh;
  const mg = mc.getContext('2d'); mg.drawImage(img, 4, 4, gridW, img.height * s);
  const md = mg.getImageData(0, 0, gw, gh).data, v = new Float64Array(gw * gh);
  for (let i = 0; i < v.length; i++) v[i] = md[i * 4 + 3] / 255;
  const polys = d3contours().size([gw, gh]).smooth(true).thresholds([0.5])(v)[0].coordinates;
  let minX = 1e9, minY = 1e9, maxX = -1e9, maxY = -1e9;
  for (const p of polys) for (const [x, y] of p[0]) { minX = Math.min(minX, x); maxX = Math.max(maxX, x); minY = Math.min(minY, y); maxY = Math.max(maxY, y); }
  const k = inches * 0.0254 / Math.max(maxX - minX, maxY - minY), cx = (minX + maxX) / 2, cy = (minY + maxY) / 2;   // fits an N×N box
  const shapes = [];
  for (const p of polys) {
    const pts = p[0].map(([x, y]) => new THREE.Vector2((x - cx) * k, -(y - cy) * k));
    if (Math.abs(THREE.ShapeUtils.area(pts)) > Math.pow(k * 4, 2)) shapes.push(new THREE.Shape(pts));
  }
  const tc = document.createElement('canvas'); tc.width = 2048; tc.height = Math.round(2048 * img.height / img.width);
  const tg = tc.getContext('2d');
  for (const r of [14, 8, 4]) for (let i = 0; i < 16; i++) { const a = i / 16 * Math.PI * 2; tg.drawImage(img, Math.cos(a) * r, Math.sin(a) * r, tc.width, tc.height); }
  tg.drawImage(img, 0, 0, tc.width, tc.height);
  const tex = new THREE.CanvasTexture(tc); tex.colorSpace = THREE.SRGBColorSpace; tex.userData.mimeType = 'image/jpeg';
  const geo = new THREE.ExtrudeGeometry(shapes, { depth: mm / 1000, bevelEnabled: true, bevelThickness: 0.0006, bevelSize: 0.0006, bevelSegments: 2, curveSegments: 2 });
  const pos = geo.attributes.position, uv = geo.attributes.uv;
  for (let i = 0; i < pos.count; i++) {
    const gx = pos.getX(i) / k + cx, gy = -pos.getY(i) / k + cy;
    uv.setXY(i, ((gx - 4) / s) / img.width, 1 - ((gy - 4) / s) / img.height);
  }
  const scene = new THREE.Scene();
  scene.add(new THREE.Mesh(geo, [
    new THREE.MeshStandardMaterial({ map: tex, roughness: 0.38, metalness: 0.15 }),
    new THREE.MeshStandardMaterial({ color: edgeColor(img), roughness: 0.55, metalness: 0.1 }),
  ]));
  const glb = await new GLTFExporter().parseAsync(scene, { binary: true, maxTextureSize: 2048 });
  return { url: URL.createObjectURL(new Blob([glb], { type: 'model/gltf-binary' })), heightIn: (maxY - minY) * k / 0.0254, widthIn: (maxX - minX) * k / 0.0254 };
}

function init(root) {
  const cfg = root.dataset, input = root.querySelector('.cgs__input'), go = root.querySelector('.cgs__go');
  const msg = root.querySelector('.cgs__msg'), mv = root.querySelector('.cgs__mv'), dims = root.querySelector('.cgs__dims');
  const loading = root.querySelector('.cgs__loading'), bar = root.querySelector('.cgs__bar i');
  const histWrap = root.querySelector('.cgs__hist'), chips = root.querySelector('.cgs__chips');
  const arBig = root.querySelector('.cgs__arbig'), qr = root.querySelector('.cgs__qr');
  const gift = root.querySelector('.cgs__gift'), share = root.querySelector('.cgs__share'), shareMsg = root.querySelector('.cgs__sharemsg');
  const form = root.closest('form[action*="/cart/add"]') || document.querySelector('.main-product-form') || document.querySelector('form[action*="/cart/add"]');
  let HKEY = 'cg-sign-hist:' + cfg.sign;
  let hist = store.get(HKEY, []), current = { img: null, name: '', url: '' }, lastUrl = '';
  let vid = store.get('cg-sign-vid', null); if (!vid) { vid = Math.random().toString(36).slice(2) + Date.now().toString(36); store.set('cg-sign-vid', vid); }

  const say = (t, err) => { msg.textContent = t; msg.classList.toggle('is-error', !!err); };
  const setProp = (k, val) => {
    if (!form) return; let el = form.querySelector(`input[name="properties[${k}]"]`);
    if (!val) { if (el) el.remove(); return; }
    if (!el) { el = document.createElement('input'); el.type = 'hidden'; el.name = `properties[${k}]`; form.appendChild(el); }
    el.value = val;
  };
  const inches = () => {
    const sel = [...(form ? form.querySelectorAll('input[type=radio]:checked, select') : []), ...document.querySelectorAll('.main-product-form input:checked, variant-selects input:checked, variant-radios input:checked, .product-form__input select, variant-selects select')];
    for (const el of sel) { const m = String(el.value).match(/(\d{1,2})\s*(?:"|”|in|inch)/i); if (m) return +m[1]; }
    return 18;
  };
  let seq = 0;
  async function show(img) {
    if (!img) return;
    current.img = img;
    const my = ++seq, w = inches();
    const { url, heightIn, widthIn } = await buildGLB(img, w);
    if (my !== seq) { URL.revokeObjectURL(url); return; }   // a newer build won; drop this one
    const prev = lastUrl; lastUrl = url; mv.src = url;
    if (prev) mv.addEventListener('load', () => URL.revokeObjectURL(prev), { once: true });
    dims.textContent = `${widthIn.toFixed(1)}" × ${heightIn.toFixed(1)}"`;
  }
  function renderHist() {
    histWrap.hidden = !hist.length;
    chips.innerHTML = hist.map((h) => `<button type="button" data-n="${h.name}" aria-pressed="${h.name === current.name}">${h.name}</button>`).join('');
  }
  function reveal(img, name) {
    if (!gift || cfg.gift !== 'true') return;
    gift.querySelector('.cgs__reveal').src = img.toDataURL('image/png');
    gift.querySelector('.cgs__giftname').textContent = `Merry Christmas, ${name}`;
    gift.hidden = false; gift.classList.remove('is-open'); void gift.offsetWidth; gift.classList.add('is-open');
    clearTimeout(reveal.t); reveal.t = setTimeout(() => { gift.hidden = true; gift.classList.remove('is-open'); }, 3600);
  }
  const shareUrl = (name) => { const u = new URL(location.href); u.searchParams.set('name', name); return u.toString(); };
  async function useResult(name, url, note, fresh, png) {
    const img = keyGreen(await loadImg(url));
    if (fresh) reveal(img, name);
    if (share) { share.hidden = false; shareMsg.textContent = ''; }
    if (arBig) arBig.querySelector('.cgs__arbig-t').textContent = arBig.dataset.arLabel.replace('[name]', name);
    current.name = name; current.url = url;
    input.value = name; input.classList.remove('is-error');
    setProp('Name', name); setProp('_Preview', url); setProp('_thumb', png || ''); current.png = png || '';
    await show(img); renderHist(); say(note);
    // let other sections (e.g. the room view) show the shopper's own name
    document.dispatchEvent(new CustomEvent('cg-sign:preview', { detail: { name, src: img.toDataURL('image/png') } }));
  }
  async function preview() {
    const name = input.value.toUpperCase().replace(NAME_RE, '');
    if (name.length < 2) { input.classList.add('is-error'); say('Type a name (at least 2 letters, no spaces).', true); input.focus(); return; }
    const saved = hist.find((h) => h.name === name);
    if (saved && saved.png && !/\.cut\.png$/.test(saved.url)) return useResult(name, saved.url, `Showing your saved preview “${name}”.`, false, saved.png);   // older saves (no cut-out) go through the API once: cached, free
    go.disabled = true; loading.hidden = false; say('');
    const t0 = Date.now(); const tick = setInterval(() => { bar.style.width = (4 + 90 * (1 - Math.exp(-(Date.now() - t0) / 9000))) + '%'; }, 300);
    try {
      const base = await loadImg(cfg.base);
      const r = await fetch(cfg.endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ image: onGreen(base), sign: cfg.sign, oldName: cfg.old, name, vid }) });
      const j = await r.json().catch(() => ({ ok: false, error: 'Preview service unavailable' }));
      if (!j.ok) throw new Error(j.error || 'Preview failed');
      hist = [{ name, url: j.url, png: j.png || '' }, ...hist.filter((h) => h.name !== name)].slice(0, 20); store.set(HKEY, hist);
      const left = typeof j.left === 'number' ? ` ${j.left} new preview${j.left === 1 ? '' : 's'} left today.` : '';
      bar.style.width = '100%';
      await useResult(name, j.url, j.cached ? `Showing your saved preview “${name}”.` : `“${name}” is ready — check the spelling, then tap “See it on your wall”.${left}`, !j.cached, j.png);
    } catch (e) { say(e.message, true); }
    finally { clearInterval(tick); go.disabled = false; setTimeout(() => { loading.hidden = true; bar.style.width = '0'; }, 350); }
  }

  input.setAttribute('maxlength', cfg.max || 12);
  input.addEventListener('keydown', (e) => {
    if (e.key === ' ') { e.preventDefault(); say('No spaces — the name is cut as one piece. Try “DEANDRE”.', true); }
    if (e.key === 'Enter') { e.preventDefault(); preview(); }
  });
  input.addEventListener('input', () => {
    const v = input.value.toUpperCase().replace(NAME_RE, ''); if (v !== input.value) input.value = v;
    input.classList.remove('is-error');
    // typed name differs from the previewed one → order carries the typed name, no stale preview
    setProp('Name', v); setProp('_Preview', v && v === current.name ? current.url : ''); setProp('_thumb', v && v === current.name ? current.png : '');
  });
  go.addEventListener('click', preview);
  // ---- AR: phones open AR directly; desktops get a QR code that opens this page (with the name) on the phone
  const canAR = () => mv.canActivateAR === true;
  async function showQR() {
    const url = (() => { const u = new URL(location.href); if (current.name) u.searchParams.set('name', current.name); u.searchParams.set('ar', '1'); return u.toString(); })();
    if (!window.qrcode) await new Promise((res, rej) => { const sc = document.createElement('script'); sc.src = 'https://cdn.jsdelivr.net/npm/qrcode-generator@1.4.4/qrcode.js'; sc.onload = res; sc.onerror = rej; document.head.appendChild(sc); });
    const q = window.qrcode(0, 'M'); q.addData(url); q.make();
    qr.querySelector('.cgs__qrcode').innerHTML = q.createSvgTag({ cellSize: 6, margin: 2, scalable: true });
    qr.hidden = false; qr.querySelector('.cgs__qrx').focus();
  }
  if (arBig) arBig.addEventListener('click', () => { if (canAR()) mv.activateAR(); else showQR().catch(() => say('Open this page on your phone to see it in AR.', true)); });
  if (qr) { qr.addEventListener('click', (e) => { if (e.target === qr || e.target.closest('.cgs__qrx')) qr.hidden = true; }); document.addEventListener('keydown', (e) => { if (e.key === 'Escape') qr.hidden = true; }); }
  // arrived from the QR code: offer a one-tap AR start once the model is ready (AR needs a tap)
  if (new URLSearchParams(location.search).get('ar') === '1') {
    mv.addEventListener('load', function once() {
      const want = (new URLSearchParams(location.search).get('name') || '').toUpperCase().replace(NAME_RE, '');
      if (want.length >= 2 && current.name !== want) return;          // wait for the shopper's name, not the sample
      mv.removeEventListener('load', once);
      if (!canAR() || root.querySelector('.cgs__artap')) return;
      const o = document.createElement('div'); o.className = 'cgs__artap'; o.innerHTML = '<button type="button">Tap to see it on your wall</button>';
      o.querySelector('button').addEventListener('click', () => { o.remove(); mv.activateAR(); });
      root.querySelector('.cgs__stage').appendChild(o); root.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }
  if (share) share.querySelector('.cgs__sharebtn').addEventListener('click', async () => {
    const url = shareUrl(current.name), title = `${current.name} — custom wall sign`;
    try {
      if (navigator.share) { await navigator.share({ title, text: `Look what I made: ${current.name} on our wall 🎄`, url }); shareMsg.textContent = 'Shared.'; return; }
      await navigator.clipboard.writeText(url); shareMsg.textContent = 'Link copied — send it to whoever you are surprising (or not).';
    } catch (e) { if (e && e.name === 'AbortError') return; shareMsg.textContent = url; }
  });
  chips.addEventListener('click', (e) => { const b = e.target.closest('button'); if (!b) return; const h = hist.find((x) => x.name === b.dataset.n); if (!h) return; if (h.png) useResult(h.name, h.url, `Showing your saved preview “${h.name}”.`, false, h.png); else { input.value = h.name; preview(); } });

  // Add to cart needs a name
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('.product-form__submit, .sticky-atc__add_button, [name="add"]'); if (!btn) return;
    if (input.value.toUpperCase().replace(NAME_RE, '').length >= 2) return;
    e.preventDefault(); e.stopImmediatePropagation();
    input.classList.add('is-error'); say('Add the name for your sign first.', true);
    root.scrollIntoView({ behavior: 'smooth', block: 'center' }); setTimeout(() => input.focus({ preventScroll: true }), 400);
  }, true);
  // size change → rebuild at the new width
  // (re-check after the delay: a team change clears current.img while its new artwork loads)
  document.addEventListener('change', (e) => { if (current.img && e.target.closest('form, variant-selects, variant-radios')) setTimeout(() => { if (current.img) show(current.img); }, 120); });

  // the product page switched team → new artwork, cache key, original name and history
  document.addEventListener('cg-sign:team', (e) => {
    const d = e.detail || {}; if (!d.art || d.sign === cfg.sign) return;
    cfg.base = d.art; cfg.sign = d.sign; cfg.old = d.old || '';
    HKEY = 'cg-sign-hist:' + cfg.sign; hist = store.get(HKEY, []);
    const typed = input.value.toUpperCase().replace(NAME_RE, '');
    current = { img: null, name: '', url: '', png: '' }; setProp('_Preview', ''); setProp('_thumb', ''); setProp('Name', typed);
    if (arBig) arBig.querySelector('.cgs__arbig-t').textContent = arBig.dataset.arLabel.replace('[name]', 'it');
    renderHist(); say(typed.length >= 2 ? `Tap “${go.textContent.trim()}” to see “${typed}” on this team’s sign.` : '');
    loadImg(cfg.base).then(show).catch((err) => say(err.message, true));
  });
  // AFC ⇄ NFC: the sibling listing's teams become extra chips in the same row (all chips A–Z); a tap opens that
  // listing with the team picked (?team=) and the typed name carried over (?n=, no auto preview)
  let EXT = []; try { EXT = JSON.parse(cfg.ext || '[]'); } catch (e) {}
  const teamRadio = [...document.querySelectorAll('input[type=radio]')].find((r) => (r.name || '').indexOf(cfg.teamOption || 'Team') === 0);
  const row = teamRadio && teamRadio.closest('.color-swatches-container');
  if (row && EXT.length && !row.dataset.cgExt) {
    row.dataset.cgExt = 1;
    EXT.forEach((t) => {
      const a = document.createElement('a'); a.className = 'color-swatch cg-team-ext'; a.href = t.url;   // the sibling's own variant of that team (server-rendered selection)
      a.title = t.name; a.dataset.team = t.name;
      const bg = t.logo ? `no-repeat center / 70% auto url(${t.logo}?width=160) var(--cg-swatch-bg, #f2efea)` : 'var(--cg-swatch-bg, #f2efea)';
      a.innerHTML = `<div class="color-swatch__image"><div class="color-swatch__custom-color cg-team-swatch" style="--bg-color: ${bg}">&nbsp;</div></div><span class="color-swatch__label"></span>`;
      a.querySelector('.color-swatch__label').textContent = t.name;
      a.addEventListener('click', (e) => { const v = input.value.toUpperCase().replace(NAME_RE, ''); if (v.length >= 2) { e.preventDefault(); location.href = a.href + (a.href.indexOf('?') < 0 ? '?' : '&') + 'n=' + encodeURIComponent(v); } });
      row.appendChild(a);
    });
    const nameOf = (el) => el.dataset.team || (el.querySelector('input[type=radio]') || {}).value || '';
    const all = [...row.children].filter((el) => el.classList.contains('color-swatch'));
    all.map(nameOf).sort().forEach((n, i) => { const el = all.find((x) => nameOf(x) === n); if (el) el.style.order = i; });
  }
  // landed from the sibling listing (its link already selects the team's variant): carry the typed name
  const qp = new URLSearchParams(location.search);
  if (qp.get('n')) { input.value = qp.get('n').toUpperCase().replace(NAME_RE, '').slice(0, +cfg.max || 12); input.dispatchEvent(new Event('input', { bubbles: true })); }
  // bridge for the theme's variant picker (Team chips): data-teams on the block maps team → artwork/name/glow.
  // A team may list extra patterns (fin: "marble,holo,…"): same sign in another finish → own artwork, AI key and photo.
  let TEAMS = null; try { TEAMS = cfg.teams ? JSON.parse(cfg.teams) : null; } catch (e) {}
  if (TEAMS) {
    const opt = cfg.teamOption || 'Team';
    const assetBase = (cfg.base || '').replace(/[^/]+$/, '');
    const pat = root.querySelector('.cgs__pat'), pats = root.querySelector('.cgs__pats'), patName = root.querySelector('.cgs__patname');
    const LABEL = { '': 'Signature', marble: 'Marble', holo: 'Holo', glitter: 'Glitter', brushed: 'Brushed', swirl: 'Pearl', flame: 'Flame', leopard: 'Leopard', tiger: 'Tiger', gold: 'Gold' };
    let team = '', fin = '';
    // Light option: any value except "No LED" = backlit → the LED photos
    const lightOpt = cfg.lightOption || 'Light';
    const isLed = () => { const r = [...document.querySelectorAll('input[type=radio]:checked')].find((x) => (x.name || '').indexOf(lightOpt) === 0); return !!r && !/^no\b/i.test(r.value); };
    const finsOf = (c) => ['', ...String(c.fin || '').split(',').map((f) => f.trim()).filter(Boolean)];
    const photoOf = (c, f, led) => (c.photo || '').replace(/-((?:v2-)?)main\.jpg/, (m, v) => '-' + (f || v) + (led ? 'led' : f ? '' : 'main') + '.jpg');   // -main / -led / -marble / -marbleled
    const thumb = (u) => u.replace(/([?&])width=\d+/, '$1width=200');
    // show the matching photo in the product gallery (media alt = team, file name = …-<team>-<finish>.jpg)
    const galleryTo = (c, f) => {
      const file = (photoOf(c, f, isLed()).split('/').pop() || '').split('?')[0]; if (!file) return;
      const li = [...document.querySelectorAll('media-gallery li[data-media-id]')].find((x) => [...x.querySelectorAll('img')].some((i) => (i.currentSrc || i.src || '').includes('/' + file)));
      const g = li && li.closest('media-gallery'); if (g && g.setActiveMedia) g.setActiveMedia(li.dataset.mediaId, false);
    };
    const renderPats = (c) => {
      const fs = finsOf(c); pat.hidden = fs.length < 2;
      pats.style.setProperty('--cols', fs.length <= 6 ? fs.length : Math.ceil(fs.length / 2));   // 7–8 patterns → two rows
      pats.innerHTML = fs.map((f) => `<button type="button" role="radio" data-f="${f}" aria-checked="${f === fin}"><i><img src="${thumb(photoOf(c, f, isLed()))}" alt="" loading="lazy" width="100" height="100"></i><span>${LABEL[f] || f}</span></button>`).join('');
      patName.textContent = LABEL[fin] || fin;
    };
    const fireTeam = (t, f) => {
      const c = TEAMS[t]; if (!c) return;
      team = t; fin = finsOf(c).includes(f) ? f : '';
      renderPats(c); setProp('Pattern', LABEL[fin] || fin); lightUp(c);
      // patterns: every finish of this team with its day + LED photo (Pattern Lab and other sections build from this)
      const patterns = finsOf(c).map((x) => ({ f: x, label: LABEL[x] || x, day: photoOf(c, x, false), night: photoOf(c, x, true) }));
      document.dispatchEvent(new CustomEvent('cg-sign:team', { detail: { team: t, key: c.key, finish: fin, patterns, led: isLed(),
        sign: 'nfl-' + c.key + (fin ? '-' + fin : c.rev ? '-' + c.rev : ''), old: c.old, glow: c.glow,
        art: assetBase + 'cg-sign-nfl-' + c.key + (fin ? '-' + fin : '') + '.webp', photo: photoOf(c, fin) } }));
    };
    // LED only changes the photos (gallery, pattern chips, cart image); the 3D preview stays a plain daylight view
    const lightUp = (c) => { setProp('_img', thumb(photoOf(c, fin, isLed())).replace('width=200', 'width=600')); };
    // other sections (Pattern Lab) can pick a pattern: same as tapping its chip here
    document.addEventListener('cg-sign:pick', (e) => { const f = (e.detail || {}).finish || ''; if (TEAMS[team] && f !== fin) { fireTeam(team, f); galleryTo(TEAMS[team], fin); } });
    pats.addEventListener('click', (e) => {
      const b = e.target.closest('button[data-f]'); if (!b || b.dataset.f === fin) return;
      fireTeam(team, b.dataset.f); galleryTo(TEAMS[team], fin);
    });
    document.addEventListener('change', (e) => {
      const t = e.target; if (!t || t.type !== 'radio') return;
      if ((t.name || '').indexOf(opt) === 0) fireTeam(t.value, fin);
      else if ((t.name || '').indexOf(lightOpt) === 0 && TEAMS[team]) { renderPats(TEAMS[team]); lightUp(TEAMS[team]); document.dispatchEvent(new CustomEvent('cg-sign:light', { detail: { led: isLed() } })); }
      else return;
      // after the theme has switched to the variant's photo, show the chosen pattern (LED or not)
      if (TEAMS[team] && (fin || isLed())) setTimeout(() => galleryTo(TEAMS[team], fin), 260);
    });
    const first = [...document.querySelectorAll('input[type=radio]:checked')].find((r) => (r.name || '').indexOf(opt) === 0 && TEAMS[r.value]);
    if (first) setTimeout(() => fireTeam(first.value, ''), 0);
  }
  // coupon mode (list = 2x, COLDGEAR50 applied for the shopper): the theme writes the LIST price into the add-to-cart
  // buttons after every variant change, so swap it for what the shopper pays. Shown prices never equal a list price.
  const pct = +cfg.coupon || 0;
  if (pct) {
    const fixAtc = () => document.querySelectorAll('.product-form__submit, .sticky-atc__add_button, [name="add"]').forEach((btn) => {
      // the theme also resets the custom label ("ADD MY SIGN TO CART") to "Add to cart" on variant change
      const lab = btn.querySelector('.main-atc__label__text');
      if (lab && btn.dataset.customLabel && /^add to cart$/i.test(lab.textContent.trim())) lab.textContent = btn.dataset.customLabel;
      const w = document.createTreeWalker(btn, NodeFilter.SHOW_TEXT); let n;
      while ((n = w.nextNode())) {
        const m = n.nodeValue.match(/\$([\d,]+\.\d{2})/); if (!m || m[0] === btn.dataset.cgShown) continue;
        const cents = Math.round(parseFloat(m[1].replace(/,/g, '')) * 100), pay = cents - Math.floor(cents * pct / 100);
        const txt = '$' + (pay / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        btn.dataset.cgShown = txt; n.nodeValue = n.nodeValue.replace(m[0], txt);
      }
    });
    fixAtc();
    new MutationObserver(() => { clearTimeout(fixAtc.t); fixAtc.t = setTimeout(fixAtc, 30); })
      .observe(document.querySelector('product-info, main') || document.body, { subtree: true, childList: true, characterData: true });
  }
  renderHist();
  const shared = (new URLSearchParams(location.search).get('name') || '').toUpperCase().replace(NAME_RE, '');
  loadImg(cfg.base).then(show).catch((e) => say(e.message, true)).then(() => {
    if (shared.length >= 2) { input.value = shared; preview(); }
  });
}

document.querySelectorAll('.cgs').forEach(init);
document.addEventListener('shopify:block:select', () => document.querySelectorAll('.cgs:not([data-ready])').forEach((r) => { r.dataset.ready = 1; init(r); }));
